import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../auth/state';
import './admin_meet_mn.css';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { FaCalendarAlt, FaEdit, FaLink } from 'react-icons/fa'; // # Import additional icons
import sessionManager from '../../../auth/session';
import cacheManager from '../../../auth/cache_manager';
import { db, auth } from '../../../auth/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

function AdminMeetingsMain() {
  const navigate = useNavigate();
  const { userProfile } = useAuthStore();
  const [profileData, setProfileData] = useState(null);
  const [meetingUrl, setMeetingUrl] = useState('');
  const [isEditingUrl, setIsEditingUrl] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (auth.currentUser?.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, []);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (auth.currentUser?.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${auth.currentUser.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Fetch user data and mentor data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // # Try to get data from session first
        const session = sessionManager.getSession();
        if (session?.user) {
          setProfileData(session.user);
        }

        // # If not in session, try cache
        const uid = userProfile?.uid;
        if (uid) {
          const cachedData = await cacheManager.initializeUserCache(uid);
          if (cachedData) {
            setProfileData(cachedData);
          }

          // # Fetch mentor data
          const mentorDoc = await getDoc(doc(db, 'mentors', uid));
          if (mentorDoc.exists()) {
            const mentorData = mentorDoc.data();
            setMeetingUrl(mentorData.personal?.zoom_url || '');
          }
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load profile data');
      }
    };

    fetchData();
  }, [userProfile]);

  // # Handle meeting URL update
  const handleUrlUpdate = async (newUrl) => {
    if (!userProfile?.uid) {
      setError('User not authenticated');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const mentorRef = doc(db, 'mentors', userProfile.uid);
      
      // # Validate if mentor document exists
      const mentorDoc = await getDoc(mentorRef);
      if (!mentorDoc.exists()) {
        throw new Error('Mentor profile not found');
      }

      // # Update the zoom_url in the personal map
      await updateDoc(mentorRef, {
        'personal.zoom_url': newUrl,
        'metadata.last_edited_at': new Date()
      });

      setMeetingUrl(newUrl);
      setIsEditingUrl(false);
    } catch (err) {
      console.error('Error updating meeting URL:', err);
      setError('Failed to update meeting URL');
    } finally {
      setIsSaving(false);
    }
  };

  // # Meeting management sections
  const meetingSections = [
    {
      title: 'General Meetings',
      icon: <FaCalendarAlt className="meeting-icon" />,
      description: 'Manage general meetings and schedules',
      path: '/admin/meetings/general'
    },
    {
      title: 'Mentorship Step 1 Meetings',
      icon: '/icons/dash/one.png',
      description: 'Manage Step 1 mentorship meetings',
      path: '/admin/meetings/step1'
    },
    {
      title: 'Mentorship Step 2 Meetings',
      icon: '/icons/dash/two.png',
      description: 'Manage Step 2 mentorship meetings',
      path: '/admin/meetings/step2'
    },
    {
      title: 'OET Meetings',
      icon: '/icons/dash/oet.png',
      description: 'Manage OET preparation meetings',
      path: '/admin/meetings/oet'
    },
    {
      title: 'Visa Counselling Meetings',
      icon: '/icons/dash/visa.png',
      description: 'Manage visa counselling sessions',
      path: '/admin/meetings/visa'
    }
  ];

  return (
    <div className={`admin_meet_container ${isDarkMode ? 'admin_meet_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <div className="admin_meet_content">
        <div className="admin_meet_header">
          <h1>Meeting Management</h1>
          <p>Manage all types of meetings and schedules</p>
        </div>

        {/* # Profile Section */}
        <div className="admin_meet_profile">
          <h2>My Meeting Profile</h2>
          <div className="admin_meet_profile_info">
            <div className="admin_meet_profile_item">
              <label>Name:</label>
              <span>{profileData?.firstName} {profileData?.lastName}</span>
            </div>
            <div className="admin_meet_profile_item">
              <label>Email:</label>
              <span>{profileData?.email}</span>
            </div>
            <div className="admin_meet_profile_item">
              <label>Meeting URL:</label>
              {isEditingUrl ? (
                <div className="admin_meet_url_edit">
                  <input
                    type="text"
                    value={meetingUrl}
                    onChange={(e) => setMeetingUrl(e.target.value)}
                    placeholder="Enter your Zoom meeting URL"
                    disabled={isSaving}
                  />
                  <button 
                    onClick={() => handleUrlUpdate(meetingUrl)}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button 
                    onClick={() => {
                      setIsEditingUrl(false);
                      setError('');
                    }}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="admin_meet_url_display">
                  <span>{meetingUrl || 'Not set'}</span>
                  <button 
                    className="admin_meet_url_edit_btn"
                    onClick={() => setIsEditingUrl(true)}
                  >
                    <FaEdit />
                  </button>
                  {meetingUrl && (
                    <button 
                      className="admin_meet_url_copy_btn"
                      onClick={() => navigator.clipboard.writeText(meetingUrl)}
                    >
                      <FaLink />
                    </button>
                  )}
                </div>
              )}
              {error && <div className="admin_meet_error">{error}</div>}
            </div>
          </div>
        </div>
        
        {/* # Meeting Types Grid */}
        <div className="admin_meet_section">
          <h2>Meeting Types</h2>
          <div className="admin_meet_grid">
            {meetingSections.map((section, index) => (
              <div 
                key={index} 
                className="admin_meet_card"
                onClick={() => navigate(section.path)}
              >
                {typeof section.icon === 'string' ? (
                  <img src={section.icon} alt={section.title} className="admin_meet_icon" />
                ) : (
                  section.icon
                )}
                <h3>{section.title}</h3>
                <p>{section.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default AdminMeetingsMain;
